import { Award, Sparkles } from "lucide-react";

interface Partner {
  name: string;
  logo: string;
  type: "partner" | "award";
}

const partners: Partner[] = [
  {
    name: "Google for Startups",
    logo: "/images/partners/google-for-startups.png",
    type: "partner",
  },
  {
    name: "AWS",
    logo: "/images/partners/aws.svg",
    type: "partner",
  },
  {
    name: "Expara Ventures",
    logo: "/images/partners/expara.png",
    type: "partner",
  },
  {
    name: "Founder Institute",
    logo: "/images/partners/founder-institute.png",
    type: "award",
  },
  {
    name: "Malaysian Global Innovation and Creativity Centre",
    logo: "/images/partners/magic.png",
    type: "partner",
  },
  {
    name: "HDBank",
    logo: "/images/partners/hdbank.png",
    type: "partner",
  },
  {
    name: "Galaxy of Innovation",
    logo: "/images/partners/galaxy-innovation.png",
    type: "award",
  },
];

export default function PartnersAwardsSection() {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-midnight-blue/5 via-electric-blue/5 to-burnt-orange/5">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />

      <div className="container mx-auto max-w-7xl px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-midnight-blue/5 px-4 py-2 rounded-full mb-4">
            <Award className="w-4 h-4 text-burnt-orange" />
            <span className="text-sm font-medium text-midnight-blue/80">
              Đối Tác & Giải Thưởng
            </span>
            <Sparkles className="w-4 h-4 text-electric-blue" />
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-midnight-blue mb-4">
            Được Công Nhận Bởi
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-burnt-orange to-electric-blue">
              Những Tổ Chức Hàng Đầu
            </span>
          </h2>

          <p className="text-lg text-midnight-blue/70 max-w-2xl mx-auto">
            Locuno tự hào được đồng hành và ghi nhận bởi các tổ chức uy tín hàng
            đầu thế giới
          </p>
        </div>

        {/* Infinite Scroll Marquee */}
        <div className="relative">
          {/* Gradient fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white via-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white via-white to-transparent z-10 pointer-events-none" />

          {/* Marquee Container */}
          <div className="overflow-hidden py-8">
            <div className="flex animate-marquee hover:pause-animation">
              {/* First set of logos */}
              {partners.map((partner, index) => (
                <LogoCard key={`first-${index}`} partner={partner} />
              ))}
              {/* Duplicate for seamless loop */}
              {partners.map((partner, index) => (
                <LogoCard key={`second-${index}`} partner={partner} />
              ))}
            </div>
          </div>
        </div>

        {/* Static Grid for Mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8 md:hidden">
          {partners.map((partner, index) => (
            <div
              key={index}
              className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-midnight-blue/10 hover:border-burnt-orange/30 transition-all duration-300 hover:shadow-lg group"
            >
              <div className="aspect-video flex items-center justify-center">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300 opacity-70 group-hover:opacity-100"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<div class="text-center"><div class="font-semibold text-midnight-blue/80 text-sm mb-1">${partner.name}</div></div>`;
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom CSS for marquee animation */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-marquee {
          animation: marquee 40s linear infinite;
          width: fit-content;
          display: flex;
          gap: 2rem;
        }

        .animate-marquee:hover.pause-animation {
          animation-play-state: paused;
        }

        .bg-grid-pattern {
          background-image:
            linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </section>
  );
}

// Logo Card Component
function LogoCard({ partner }: { partner: Partner }) {
  return (
    <div className="flex-shrink-0 w-64">
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-midnight-blue/10 hover:border-burnt-orange/30 transition-all duration-500 hover:shadow-xl hover:scale-105 group mx-4 h-32 flex items-center justify-center relative overflow-hidden">
        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <img
          src={partner.logo}
          alt={partner.name}
          className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500 opacity-60 group-hover:opacity-100 relative z-10"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML += `<div class="text-center relative z-10"><div class="font-semibold text-midnight-blue/80 text-sm">${partner.name}</div></div>`;
            }
          }}
        />
      </div>
    </div>
  );
}
